
import DsModal from '@modules/design-system/components/organisms/modals/Modal.vue';
import Loader from '@modules/husleie-framework/src/components/loaders/Loader.vue';
import ApiErrorModal from '@modules/husleie-framework/src/components/modals/ApiErrorModal.vue';
import AppError from '@modules/husleie-framework/src/components/molecules/app/AppError.vue';
import AppWarning from '@modules/husleie-framework/src/components/molecules/app/AppWarning.vue';
import AppLoader from '@modules/husleie-framework/src/mixins/AppLoader';
import AppClass from '@modules/husleie-framework/src/static-classes/App.class';
import type AppConfig from '@modules/husleie-framework/src/types/AppConfig.entity';
import { I18N } from '@modules/husleie-framework/src/types/I18N.entity';
import * as VeeValidate from 'vee-validate';
import Vue, { VueConstructor } from 'vue';
import VueI18n from 'vue-i18n';
import { MetaInfo } from 'vue-meta';
import { mapGetters, mapMutations } from 'vuex';

interface ComponentData {
  appMessage: undefined | null | string;
  appMessageIsError: boolean;
  appMessageIsWarning: boolean;
  appName: string;
  appRoute: string;
}

export default (Vue as VueConstructor<Vue & InstanceType<typeof AppLoader>>).extend({
  name: 'MainApp',
  mixins: [AppLoader],
  components: {
    'hl-AppWarning': AppWarning,
    'hl-AppError': AppError,
    'hl-Loader': Loader,
    'hl-ApiErrorModal': ApiErrorModal,
    'o-Modal': DsModal,
  },
  props: {
    config: {
      type: Object as () => AppConfig,
      required: true,
    },
  },
  data(): ComponentData {
    return {
      appMessage: null,
      appMessageIsError: false,
      appMessageIsWarning: false,
      appName: '',
      appRoute: '',
    };
  },
  metaInfo(): MetaInfo {
    const title = this.platformName ? `${this.appTabTitle} - ${this.platformName}` : this.appTabTitle;
    return this.getMetaInfo(title, this.getLocale, this.config.loadZendesk);
  },
  watch: {
    config: {
      handler(configData) {
        if (configData.getValidationRules && configData.getFeatures) {
          this.loadApplication(
            AppClass.getName(),
            this.appI18n(this.$i18n),
            VeeValidate.extend,
            configData.getValidationRules,
            configData.getFeatures,
            configData.loadStaticData
          );
        }
      },
      immediate: true,
      deep: true,
    },
    '$i18n.locale': {
      handler() {
        this.setLocale(this.$i18n.locale);
        this.setLanguage(this.$i18n.locale);
      },
      immediate: true,
      deep: true,
    },
    '$route.name': {
      handler(name: string) {
        this.appName = `app-${AppClass.getName()?.toLowerCase()}`;
        if (name) {
          this.appRoute = `app-route-${name?.toLowerCase()}`;
          this.setCurrentActiveRouteByName(name);
        }
      },
      immediate: true,
    },
  },
  created() {
    const { setSession, getSession } = this.config;
    if (setSession) {
      window.onbeforeunload = () => {
        setSession();
      };
    }
    if (getSession) {
      getSession();
    }
  },
  mounted() {
    this.setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);

    if (AppClass.hasGlobalMessage()) {
      if (AppClass.isGlobalMessageError()) {
        this.appMessageIsError = true;
        this.appMessage = AppClass.getGlobalMessage();
      } else if (AppClass.isGlobalMessageWarning()) {
        this.appMessageIsWarning = true;
        this.appMessage = AppClass.getGlobalMessage();
      }
    }
  },
  computed: {
    ...mapGetters('common/staticData', ['getApiError', 'getModalIsOpen', 'getLocale']),

    appTabTitle(): string {
      let tabTitle: string;
      if (this.appDataLoaded!) {
        let title: string = this.translate('browser.tab.title');
        if (!title || title === 'title') {
          title = AppClass.getName();
        }
        tabTitle = title;
      } else {
        tabTitle = 'App';
      }
      return tabTitle;
    },
  },
  methods: {
    ...mapMutations('common/appRoutes', ['setCurrentActiveRouteByName']),
    ...mapMutations('common/staticData', ['setLocale', 'setLanguage', 'setTimezone']),

    appI18n(i18n: VueI18n): I18N {
      return i18n as unknown as I18N;
    },
  },
});
